import React, { useState } from 'react';
import { Button, Tooltip, IconButton } from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';
import { toast } from 'react-toastify';

interface CopyLinkButtonProps {
  linkUrl: string;
  variant?: 'button' | 'icon';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({
  linkUrl,
  variant = 'button',
  size = 'medium',
  fullWidth = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast.error('Failed to copy link');
    }
  };

  if (variant === 'icon') {
    return (
      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
        <IconButton
          onClick={handleCopy}
          size={size}
          color={copied ? 'success' : 'primary'}
          sx={{
            transition: 'all 0.3s ease',
          }}
        >
          {copied ? <Check /> : <ContentCopy />}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      variant="outlined"
      size={size}
      fullWidth={fullWidth}
      startIcon={copied ? <Check /> : <ContentCopy />}
      onClick={handleCopy}
      color={copied ? 'success' : 'primary'}
      sx={{
        transition: 'all 0.3s ease',
        minHeight: size === 'large' ? 48 : size === 'medium' ? 44 : 36,
      }}
    >
      {copied ? 'Copied!' : 'Copy Link'}
    </Button>
  );
};

export default CopyLinkButton;
